import {useEffect, useMemo, useState} from 'react'
import {localDateForInput, localStartOfDayIso, localEndOfDayIso} from '@/lib/date-utils'
import {
    NonResponseAction,
    ReviewerType,
    type CertificationPolicy,
    type CertificationPolicyConditionRequest,
    type CreateCertificationPolicyRequest,
    type UpdateCertificationPolicyRequest,
} from '@/types/certification'
import {ScopeMode, type ScopeMode as ScopeModeType} from '@/features/certification/scope-mode'

/** A single structured-conditions row in the scope editor. */
export interface ScopeConditionFormEntry {
    field: string
    operator: string
    value: string
}

/** Wizard step model — spec §5.2. */
export const WizardStep = {BASICS: 0, SCOPE: 1, REVIEW_SETTINGS: 2, LAUNCH: 3} as const
export type WizardStep = (typeof WizardStep)[keyof typeof WizardStep]

/**
 * Form slice owned by the certification-policy wizard. Mirrors the
 * `PolicyFormState` shape from the legacy flat form (CertificationPolicyDetail)
 * extended with the new validFrom/validTo validity window. Scope (conditions /
 * expression) is intentionally absent in this skeleton — it lands in 949.03.
 */
export interface WizardFormState {
    name: string
    description: string
    priority: number
    active: boolean
    validFrom: string
    validTo: string
    scopeMode: ScopeModeType
    conditions: ScopeConditionFormEntry[]
    conditionExpr: string
    reviewerType: ReviewerType
    overrideReviewFrequency: boolean
    reviewFrequencyDays: number
    gracePeriodDays: number
    nonResponseAction: NonResponseAction
    autoRevokeOnOverdue: boolean
    ownerPartyId: string | null
}

function defaultFormState(): WizardFormState {
    return {
        name: '',
        description: '',
        priority: 0,
        active: true,
        validFrom: '',
        validTo: '',
        scopeMode: ScopeMode.CONDITIONS,
        conditions: [],
        conditionExpr: '',
        reviewerType: ReviewerType.MANAGER,
        overrideReviewFrequency: false,
        reviewFrequencyDays: 90,
        gracePeriodDays: 14,
        nonResponseAction: NonResponseAction.NO_CHANGE,
        autoRevokeOnOverdue: false,
        ownerPartyId: null,
    }
}

function policyToFormState(policy: CertificationPolicy): WizardFormState {
    return {
        name: policy.name,
        description: policy.description ?? '',
        priority: policy.priority,
        active: policy.active,
        validFrom: localDateForInput(policy.validFrom),
        validTo: localDateForInput(policy.validTo),
        scopeMode: policy.conditionExpr != null ? ScopeMode.EXPRESSION : ScopeMode.CONDITIONS,
        conditions: (policy.conditions ?? []).map((c) => ({
            field: c.field,
            operator: c.operator,
            value: c.value,
        })),
        conditionExpr: policy.conditionExpr ?? '',
        reviewerType: policy.parameter?.reviewerType ?? ReviewerType.MANAGER,
        overrideReviewFrequency: policy.parameter?.reviewFrequencyDays != null,
        reviewFrequencyDays: policy.parameter?.reviewFrequencyDays ?? 90,
        gracePeriodDays: policy.parameter?.gracePeriodDays ?? 14,
        nonResponseAction: policy.parameter?.nonResponseAction ?? NonResponseAction.NO_CHANGE,
        autoRevokeOnOverdue: policy.parameter?.autoRevokeOnOverdue ?? false,
        ownerPartyId: policy.parameter?.owner?.id ?? null,
    }
}

/** True when both dates are set and validTo is not strictly after validFrom. */
export function isValidToBeforeValidFrom(validFrom: string, validTo: string): boolean {
    if (!validFrom || !validTo) return false
    return validTo <= validFrom
}

function buildScope(form: WizardFormState): Pick<CreateCertificationPolicyRequest, 'conditions' | 'conditionExpr'> {
    if (form.scopeMode === ScopeMode.EXPRESSION) {
        return {conditionExpr: form.conditionExpr.trim()}
    }
    const conditions: CertificationPolicyConditionRequest[] = form.conditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
    }))
    return {conditions}
}

function buildRequestBody(
    form: WizardFormState,
    version?: number,
): CreateCertificationPolicyRequest | UpdateCertificationPolicyRequest {
    return {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        priority: form.priority,
        active: form.active,
        validFrom: form.validFrom ? localStartOfDayIso(form.validFrom) : undefined,
        validTo: form.validTo ? localEndOfDayIso(form.validTo) : undefined,
        ...buildScope(form),
        parameter: {
            reviewFrequencyDays: form.overrideReviewFrequency ? form.reviewFrequencyDays : undefined,
            reviewerType: form.reviewerType,
            gracePeriodDays: form.gracePeriodDays,
            nonResponseAction: form.nonResponseAction,
            autoRevokeOnOverdue: form.autoRevokeOnOverdue,
            ownerPartyId: form.ownerPartyId ?? undefined,
        },
        ...(version !== undefined ? {version} : {}),
    }
}

export interface UseCertificationPolicyWizardFormResult {
    form: WizardFormState
    initialized: boolean
    setField: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void
    validToError: boolean
    isStepValid: (step: WizardStep) => boolean
    buildBody: (version?: number) => CreateCertificationPolicyRequest | UpdateCertificationPolicyRequest
}

/**
 * Owns the wizard form slice, hydrates once from a fetched policy in edit mode,
 * and exposes per-step validity gating + request-body building.
 */
export function useCertificationPolicyWizardForm(
    policy: CertificationPolicy | undefined,
): UseCertificationPolicyWizardFormResult {
    const [form, setForm] = useState<WizardFormState>(defaultFormState)
    const [initialized, setInitialized] = useState(false)

    useEffect(() => {
        if (!policy || initialized) return
        setForm(policyToFormState(policy))
        setInitialized(true)
    }, [policy, initialized])

    const setField = <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => {
        setForm(prev => ({...prev, [key]: value}))
    }

    const validToError = isValidToBeforeValidFrom(form.validFrom, form.validTo)

    const isStepValid = (step: WizardStep): boolean => {
        switch (step) {
            case WizardStep.BASICS:
                return form.name.trim().length > 0
                    && Number.isFinite(form.priority)
                    && !validToError
            case WizardStep.REVIEW_SETTINGS:
                return !!form.reviewerType
                    && form.gracePeriodDays >= 0
                    && (!form.overrideReviewFrequency || form.reviewFrequencyDays >= 0)
            case WizardStep.SCOPE:
                // WHY: empty conditions are valid ("certify everything"); only a
                // blank expression in expression mode statically blocks Next. The
                // runtime gates (preview in-flight, server CEL 400) are layered on
                // by the wizard shell — they can't be known here (spec §5.2).
                return form.scopeMode !== ScopeMode.EXPRESSION || form.conditionExpr.trim().length > 0
            case WizardStep.LAUNCH:
                return true
            default:
                return false
        }
    }

    const buildBody = useMemo(
        () => (version?: number) => buildRequestBody(form, version),
        [form],
    )

    return {form, initialized, setField, validToError, isStepValid, buildBody}
}
